/**
 * OCR Worker Service
 *
 * Wrapper service to communicate with the OCR web worker.
 * Provides a clean API for processing images/PDFs through OCR
 * in a background thread.
 */

import type {
  OCRWorkerResult,
  ExtractedRecordData,
  RecordDocumentType,
} from '@/workers/ocr.worker';

// Re-export types for convenience
export type { OCRWorkerResult, ExtractedRecordData, RecordDocumentType };

// Progress callback type
export interface OCRProgress {
  progress: number;
  status: string;
  currentFile?: string;
}

export type ProgressCallback = (progress: OCRProgress) => void;

// Combined result for multiple files
export interface OCRCombinedResult {
  text: string;
  averageConfidence: number;
  totalProcessingTime: number;
  extractedData: ExtractedRecordData;
  individualResults: OCRWorkerResult[];
}

// Supported file types for OCR
const SUPPORTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/bmp',
  'image/webp',
  'image/tiff',
];

const SUPPORTED_DOCUMENT_TYPES = [
  'application/pdf',
];

/**
 * Check if a file is supported for OCR
 */
export function isOCRSupported(file: File): boolean {
  return (
    SUPPORTED_IMAGE_TYPES.includes(file.type) ||
    SUPPORTED_DOCUMENT_TYPES.includes(file.type)
  );
}

/**
 * Get the file type category
 */
export function getFileTypeCategory(file: File): 'image' | 'pdf' | 'unsupported' {
  if (SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    return 'image';
  }
  if (SUPPORTED_DOCUMENT_TYPES.includes(file.type)) {
    return 'pdf';
  }
  return 'unsupported';
}

/**
 * OCR Worker Service Class
 */
class OCRWorkerService {
  private worker: Worker | null = null;
  private pendingRequests: Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (error: Error) => void;
      onProgress?: ProgressCallback;
    }
  > = new Map();
  private messageIdCounter = 0;

  /**
   * Get or create the worker instance
   */
  private getWorker(): Worker {
    if (!this.worker) {
      // Create web worker using Vite's worker import syntax
      this.worker = new Worker(
        new URL('../workers/ocr.worker.ts', import.meta.url),
        { type: 'module' }
      );

      // Set up message handler
      this.worker.onmessage = (event) => {
        const { type, id, payload } = event.data;
        const request = this.pendingRequests.get(id);

        if (!request) {
          console.warn(`[OCRWorkerService] No pending request for id: ${id}`);
          return;
        }

        switch (type) {
          case 'progress':
            if (request.onProgress && payload) {
              request.onProgress({
                progress: payload.progress ?? 0,
                status: payload.status ?? '',
                currentFile: payload.currentFile,
              });
            }
            break;

          case 'result':
            this.pendingRequests.delete(id);
            request.resolve(payload);
            break;

          case 'error':
            this.pendingRequests.delete(id);
            request.reject(new Error(payload?.error ?? 'Unknown OCR error'));
            break;

          case 'terminated':
            this.pendingRequests.delete(id);
            request.resolve(undefined);
            break;
        }
      };

      // Handle worker errors
      this.worker.onerror = (error) => {
        console.error('[OCRWorkerService] Worker error:', error);
        // Reject all pending requests
        this.pendingRequests.forEach((request, id) => {
          request.reject(new Error(`Worker error: ${error.message}`));
          this.pendingRequests.delete(id);
        });
      };
    }

    return this.worker;
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `ocr-${Date.now()}-${++this.messageIdCounter}`;
  }

  /**
   * Convert File to ArrayBuffer
   */
  private async fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'));
        }
      };
      reader.onerror = () => reject(new Error('FileReader error'));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Process a single image file
   */
  async processImage(
    file: File,
    onProgress?: ProgressCallback
  ): Promise<OCRWorkerResult> {
    const fileType = getFileTypeCategory(file);

    if (fileType === 'unsupported') {
      throw new Error(`Unsupported file type: ${file.type}`);
    }

    // For PDFs, we would need to convert to images first
    // For now, we only support direct image processing
    if (fileType === 'pdf') {
      throw new Error('PDF processing requires conversion to images first. Use processImages with extracted PDF pages.');
    }

    const worker = this.getWorker();
    const messageId = this.generateMessageId();
    const imageData = await this.fileToArrayBuffer(file);

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(messageId, {
        resolve: (value) => resolve((value as { result: OCRWorkerResult }).result),
        reject,
        onProgress,
      });

      worker.postMessage({
        type: 'process',
        id: messageId,
        payload: {
          imageData,
          fileName: file.name,
          mimeType: file.type,
        },
      });
    });
  }

  /**
   * Process multiple image files
   */
  async processImages(
    files: File[],
    onProgress?: ProgressCallback
  ): Promise<OCRCombinedResult> {
    // Filter to only supported files
    const supportedFiles = files.filter((file) => {
      const type = getFileTypeCategory(file);
      return type === 'image';
    });

    if (supportedFiles.length === 0) {
      throw new Error('No supported image files provided');
    }

    const worker = this.getWorker();
    const messageId = this.generateMessageId();

    // Convert all files to ArrayBuffers
    const imagesData = await Promise.all(
      supportedFiles.map(async (file) => ({
        imageData: await this.fileToArrayBuffer(file),
        fileName: file.name,
        mimeType: file.type,
      }))
    );

    const results = await new Promise<OCRWorkerResult[]>((resolve, reject) => {
      this.pendingRequests.set(messageId, {
        resolve: (value) => resolve((value as { results: OCRWorkerResult[] }).results),
        reject,
        onProgress,
      });

      worker.postMessage({
        type: 'processMultiple',
        id: messageId,
        payload: { imagesData },
      });
    });

    // Combine results
    return this.combineResults(results);
  }

  /**
   * Combine multiple OCR results into a single result
   */
  private combineResults(results: OCRWorkerResult[]): OCRCombinedResult {
    if (results.length === 0) {
      return {
        text: '',
        averageConfidence: 0,
        totalProcessingTime: 0,
        extractedData: {
          dates: [],
          fileNumbers: [],
          signatures: 0,
          documentType: 'unknown',
        },
        individualResults: [],
      };
    }

    // Combine text with page breaks
    const combinedText = results
      .map((r, i) => `--- ${r.fileName} (Page ${i + 1}) ---\n\n${r.text}`)
      .join('\n\n');

    // Calculate averages
    const avgConfidence =
      results.reduce((sum, r) => sum + r.confidence, 0) / results.length;
    const totalTime = results.reduce((sum, r) => sum + r.processingTime, 0);

    // Merge extracted data
    const mergedData = this.mergeExtractedData(results.map((r) => r.extractedData));

    return {
      text: combinedText,
      averageConfidence: avgConfidence,
      totalProcessingTime: totalTime,
      extractedData: mergedData,
      individualResults: results,
    };
  }

  /**
   * Merge extracted data from multiple OCR results
   */
  private mergeExtractedData(dataArray: ExtractedRecordData[]): ExtractedRecordData {
    // Take the first non-undefined value for single-value fields
    const staffName = dataArray.find((d) => d.staffName)?.staffName;
    const staffId = dataArray.find((d) => d.staffId)?.staffId;
    const dateOfBirth = dataArray.find((d) => d.dateOfBirth)?.dateOfBirth;
    const appointmentDate = dataArray.find((d) => d.appointmentDate)?.appointmentDate;
    const grade = dataArray.find((d) => d.grade)?.grade;
    const salary = dataArray.find((d) => d.salary)?.salary;
    const department = dataArray.find((d) => d.department)?.department;

    // Determine document type by voting
    const typeVotes = new Map<string, number>();
    dataArray.forEach((d) => {
      if (d.documentType && d.documentType !== 'unknown') {
        typeVotes.set(d.documentType, (typeVotes.get(d.documentType) || 0) + 1);
      }
    });
    let documentType: ExtractedRecordData['documentType'] = 'unknown';
    let maxVotes = 0;
    typeVotes.forEach((votes, type) => {
      if (votes > maxVotes) {
        maxVotes = votes;
        documentType = type as ExtractedRecordData['documentType'];
      }
    });

    // Merge arrays with deduplication
    const allDates = new Set<string>();
    const allFileNumbers = new Set<string>();
    let totalSignatures = 0;

    dataArray.forEach((d) => {
      d.dates?.forEach((date) => allDates.add(date));
      d.fileNumbers?.forEach((fn) => allFileNumbers.add(fn));
      totalSignatures += d.signatures || 0;
    });

    return {
      staffName,
      staffId,
      dateOfBirth,
      appointmentDate,
      grade,
      salary,
      department,
      documentType,
      dates: Array.from(allDates),
      fileNumbers: Array.from(allFileNumbers),
      signatures: totalSignatures,
    };
  }

  /**
   * Terminate the worker and clean up resources
   */
  async terminate(): Promise<void> {
    if (!this.worker) {
      return;
    }

    const messageId = this.generateMessageId();

    await new Promise<void>((resolve, reject) => {
      this.pendingRequests.set(messageId, {
        resolve: () => resolve(),
        reject,
      });

      this.worker!.postMessage({
        type: 'terminate',
        id: messageId,
      });
    });

    // Terminate the worker thread
    this.worker.terminate();
    this.worker = null;
    this.pendingRequests.clear();

    console.log('[OCRWorkerService] Worker terminated');
  }

  /**
   * Check if the worker is active
   */
  isActive(): boolean {
    return this.worker !== null;
  }
}

// Export singleton instance
export const ocrWorkerService = new OCRWorkerService();

// Export class for testing
export { OCRWorkerService };
